import React, { useContext, useEffect, useState, useMemo, Dispatch, SetStateAction } from 'react';
import { AutocompleteGetTagProps } from '@material-ui/lab';
import MultiFilter, { StyledChip } from '../../MultiFilter';
import { LocalizationContext } from '../../LanguageContext/LocalizationContext';
import { BehaviorSubject, from } from 'rxjs';
import { concatMap, distinctUntilChanged, map } from 'rxjs/operators';
import { languageStore } from '../../../store/stores';
import { EntityResponse, Language, SelectedFilters } from '../../../types';
import timanttiService from '../../../services/timanttiService';

interface LanguageFilterProps {
    disabled?: boolean,
    languageFilters: string[],
    selectedLanguages: EntityResponse<Language>[],
    setSelectedFilters: Dispatch<SetStateAction<SelectedFilters>>,
    loadingInitialState: boolean
}

const filterSubject = new BehaviorSubject<string[]>([]);

let arrowKeySelectionOn: boolean = false;

const LanguageFilter: React.FC<LanguageFilterProps> = props => {
    const {
        disabled,
        languageFilters,
        setSelectedFilters,
        selectedLanguages,
        loadingInitialState
    } = props;
    const [languageUuids, setLanguageUuids] = useState<string[]>([]);
    const [languages, setLanguages] = useState<EntityResponse<Language>[]>([]);
    const { translation } = useContext(LocalizationContext);

    const onSelect = (_e: any, value: EntityResponse<Language>[]) => {
        setSelectedFilters((prevState: SelectedFilters) => ({
            ...prevState,
            selectedLanguages: value
        }));
    };

    useEffect(() => {
        if (languageFilters.length !== 0 && !loadingInitialState) {
            filterSubject.next(languageFilters);
            const sub = filterSubject.pipe(
                distinctUntilChanged(),
                concatMap(filters => timanttiService.getLanguages(filters)),
                map(resp => resp.data.kieli)
            ).subscribe(setLanguageUuids);
            return () => sub.unsubscribe();
        }
    }, [languageFilters, loadingInitialState]);

    useEffect(() => {
        const sub = from(languageUuids).pipe(
            distinctUntilChanged(),
            concatMap(uuid => timanttiService.getLanguage(uuid)
                .catch(_err => { })
            )
        ).subscribe();
        return () => sub.unsubscribe();
    }, [languageUuids]);

    useEffect(() => {
        const sub = languageStore.subject.subscribe(setLanguages);
        return () => sub.unsubscribe();
    }, []);

    const renderTags = (
        value: EntityResponse<Language>[],
        getTagProps: AutocompleteGetTagProps
    ) => {
        return value.map((option: any, index: number) => (
            <StyledChip
                label={option.data?.kieli} {...getTagProps({ index })}
            />
        ));
    };

    const filteredLanguages = useMemo(() =>
        languages.filter(language => languageUuids.includes(language.uuid)),
        [languages, languageUuids]
    );

    const onBlur = (e: any) => {
        e.preventDefault();
        arrowKeySelectionOn = false;
    };

    const handleKeyDown =  (e: any) => {
        if (e.key === 'Enter') {
            if (arrowKeySelectionOn) {
                arrowKeySelectionOn = false;
                return;
            } else {
                const input = e.target.value.toLowerCase();
                if (input.length > 1) {
                    const results = languages.filter(val =>
                        val.data.kieli.toLowerCase().includes(input)
                    );
                    onSelect(input, results.concat(selectedLanguages));
                }
            }
        } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            arrowKeySelectionOn = true;
        }
    };

    return (
        <MultiFilter
            disabled={disabled}
            id={"languageFilter"}
            options={filteredLanguages}
            getOptionLabel={language => language.data.kieli}
            getOptionSelected={(option, value) => option.uuid === value.uuid}
            renderTags={renderTags}
            label={translation.expertLanguage}
            placeholder={translation.choose}
            onChange={onSelect}
            value={selectedLanguages}
            onKeyDown={handleKeyDown}
            onBlur={onBlur}
        />
    );
};

export default LanguageFilter;
